/** How long the rename field waits after the last keystroke before committing. */
export const RENAME_DEBOUNCE_MS = 1000;

/**
 * Controller for the node settings panel's non-view logic. Owns the rename-commit
 * policy: renames apply on a debounce while typing, immediately on explicit apply
 * (Enter / apply button), and any still-pending rename is flushed — never dropped —
 * when the panel goes away. The commit timing here also shapes how rename undo
 * entries coalesce (TreeInteractor's `rename:<nodeId>` coalesce key), which is why
 * it lives in an interactor rather than the component.
 */
export class NodeSettingsInteractor {
    private renameTimer: ReturnType<typeof setTimeout> | null = null;
    private pendingRename: { nodeId: string; newName: string } | null = null;

    constructor(
        private readonly renameNode: (nodeId: string, newName: string) => void,
        private readonly debounceMs: number = RENAME_DEBOUNCE_MS,
    ) {}

    /** Schedules a rename to commit after the debounce, superseding any pending one. */
    scheduleRename(nodeId: string, newName: string): void {
        this.cancelPendingRename();
        this.pendingRename = { nodeId, newName };
        this.renameTimer = setTimeout(() => {
            this.renameTimer = null;
            this.flushPendingRename();
        }, this.debounceMs);
    }

    /** Commits a rename immediately (Enter / apply button), superseding any pending one. */
    applyRename(nodeId: string, newName: string): void {
        this.cancelPendingRename();
        this.renameNode(nodeId, newName);
    }

    /**
     * Commits any pending rename now. Call when the panel unmounts (deselect, panel
     * collapse, leaving edit mode) so the user's latest visible input is never dropped.
     */
    flushPendingRename(): void {
        const pending = this.pendingRename;
        this.cancelPendingRename();
        if (pending) {
            this.renameNode(pending.nodeId, pending.newName);
        }
    }

    private cancelPendingRename(): void {
        if (this.renameTimer !== null) {
            clearTimeout(this.renameTimer);
            this.renameTimer = null;
        }
        this.pendingRename = null;
    }
}
